import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import {
  Course,
  InstructionStep,
  LessonListType,
  Module,
  PresignedPost,
  Program,
  Project,
  QuizListType,
} from "utils/interfaces";
import BasicDetails from "./BasicDetails";
import AdditionalDetails from "./AdditionalDetails";
import AdditionalAttachments from "./AdditionalAttachments";
import { AnimatePresence } from "framer-motion";
import { formatUpdateLesson } from "utils/formatUpdate";
import lessonInformation from "../../utils/functions/lessonInformation";
import { createUpdateQuizzesInCourses } from "services/quizRequests";
import {
  CreateEditLessonContext,
  LessonsContext,
  MainContext,
} from "components/utils/Contexts";
import { sendFilesToS3ByPresignedPost } from "services/filesRequests";
import {
  updateRecordRequest,
  createRecordRequest,
  getCollectionRequest,
} from "services/apiRequests";
import ProgressMap from "components/main-view/utils/components/ProgressMap";
import SavingPopUp from "../pop-up-panels/saving-pop-up";
import ValidatingLessonContents from "../pop-up-panels/validation-lesson-contents";
import { usePlatform } from "components/utils/PlatformContext";
import { formatStepByStepInstructions } from "utils/instructionsFunctions";
import { sendInstructionImagesToS3 } from "../functions/sendInstructionImages";

interface SettingsProps {
  ref: any;
  selectedLesson: string;
  lessonListToShow: string;
  linkedId: string;
  linkedLesson: string;
  afterCreateLessons: (successfulLesson: string, goBack?: boolean) => void;
  headerRef: React.MutableRefObject<any>;
}

const Settings: React.FC<SettingsProps> = forwardRef(
  (
    {
      selectedLesson,
      lessonListToShow,
      linkedId,
      linkedLesson,
      afterCreateLessons,
      headerRef,
    },
    ref,
  ) => {
    // States definition
    const [id, setId] = useState("");
    const [name, setName] = useState("");
    const [level, setLevel] = useState("");
    const [description, setDescription] = useState("");
    const [expPoints, setExpPoints] = useState(0);
    const [videoUrl, setVideoUrl] = useState("");
    const [codingLanguage, setCodingLanguage] = useState("");
    const [quizzes, setQuizzes] = useState<QuizListType[]>([]);
    const [organisations, setOrganisations] = useState<string[] | undefined>();
    const [isSaving, setIsSaving] = useState(false);
    const [isValidating, setIsValidating] = useState(false);

    // Page control states
    const [stage, setStage] = useState(1);
    const [activateSaveButton, setActivateSaveButton] = useState(false);

    // Uploaded files
    const [headerImage, setHeaderImage] = useState<string | null>(null);
    const [badgeImage, setBadgeImage] = useState<string | null>(null);
    const [planDocument, setPlanDocument] = useState<string | null>(null);
    const [initialCode, setInitialCode] = useState<string | null>(null);
    const [finalCode, setFinalCode] = useState<string | null>(null);

    // Plan States definition
    const [planName, setPlanName] = useState("");
    const [planSize, setPlanSize] = useState(0);
    const [initialCodeName, setInitialCodeName] = useState("");
    const [initialCodeSize, setInitialCodeSize] = useState(0);
    const [finalCodeName, setFinalCodeName] = useState("");
    const [finalCodeSize, setFinalCodeSize] = useState(0);

    // Instructions
    const [stepByStepInstructions, setStepByStepInstructions] = useState<
      InstructionStep[]
    >([]);

    // Check content states
    const [contentToBeChecked, setContentToBeChecked] = useState<{
      [content: string]: {
        status: "loading" | "successful" | "error" | "warning";
        report?: any;
      };
    }>({});

    // Get the context
    const {
      setVersion,
      selectedData,
      setSelectedData,
    }: {
      setVersion: React.Dispatch<React.SetStateAction<number>>;
      selectedData: Project | Program | Course | Module | null;
      setSelectedData: React.Dispatch<
        React.SetStateAction<Program | Course | Project | Module | null>
      >;
    } = useContext(LessonsContext);
    // Get context parameters
    const { userRole, contextSchoolByRole } = useContext(MainContext);
    // Get the platform
    const { platform } = usePlatform();

    // Auxiliar state for change of selected quizzes
    const [baseSelectedQuizzes, setBaseSelectedQuizzes] = useState<{
      [key: string]: boolean;
    }>({});

    // Check what is the selected lessons to know if it's a 2 or 3 stage settings
    const additionalAttachs = !!["projects", "modules"].includes(
      selectedLesson,
    );

    /**
     * Functions to externalise using refs
     */
    useImperativeHandle(ref, () => ({
      // Function to present all the gathered data in the form
      provideData() {
        return provideData();
      },
      handleSaveLesson() {
        handleSave();
      },
    }));

    // Load the full
    useEffect(() => {
      const fetchQuizzes = async () => {
        if (selectedData && "quizzes" in selectedData) {
          try {
            // Fetch full quiz data for each quiz ID
            const response = await getCollectionRequest(
              `/api/quizzes/full?courseId=${selectedData._id}&activeOnly=false`,
            );

            if (response.successful && response.content) {
              setQuizzes(response.content);

              // Set the base selected quizzes
              const baseQuizzes = response.content.reduce(
                (obj: { [key: string]: boolean }, quiz: QuizListType) => {
                  obj[quiz._id] = quiz.active;
                  return obj;
                },
                {},
              );
              setBaseSelectedQuizzes(baseQuizzes);
            }
          } catch (error) {
            console.error("Error fetching quizzes:", error);
          }
        }
      };

      fetchQuizzes();
    }, [selectedData]);

    /**
     * If the settings tab is opened from an edit action (selectedData),
     * then prefill the fields
     */
    useEffect(() => {
      if (selectedData) {
        // Check if the selected data contains a document to get the size
        let planSize = 0;
        try {
          planSize = selectedData.planDocument
            ? Buffer.from(selectedData.planDocument, "base64").length
            : 0;
        } catch (error) {
          console.error("Error when trying to get the plan size:", error);
        }

        // Set the values
        setId(selectedData._id || "");
        setName(selectedData.title || "");
        setHeaderImage(selectedData.headerImage || null);
        setBadgeImage(selectedData.badgeImage || null);
        setLevel(selectedData.level || "");
        setDescription(selectedData.description || "");
        setExpPoints(selectedData.experiencePoints || 0);
        setPlanDocument(selectedData.planDocument || null);
        setPlanName("planName" in selectedData ? selectedData.planName : "");
        setPlanSize(planSize);
        setInitialCodeName(
          "initialCodeName" in selectedData ? selectedData.initialCodeName : "",
        );
        setFinalCodeName(
          "finalCodeName" in selectedData ? selectedData.finalCodeName : "",
        );
        setVideoUrl("videoUrl" in selectedData ? selectedData.videoUrl : "");
        setCodingLanguage(
          "language" in selectedData ? selectedData.language : "",
        );

        // Check if this lesson is a project or module
        if (selectedLesson === "projects" || selectedLesson === "modules") {
          setStepByStepInstructions(
            (selectedData as Project | Module).stepByStepInstructions || [],
          );
        }
      }
    }, [selectedData]);

    /**
     * Function to update the status of the organisation field based on the
     * user role
     */
    useEffect(() => {
      if (
        ["customer admin", "class admin"].includes(userRole) &&
        contextSchoolByRole &&
        contextSchoolByRole._id
      )
        setOrganisations([contextSchoolByRole._id]);
    }, [userRole]);

    /**
     * Send signal to header save button when all the form is completed
     */
    useEffect(() => {
      // Check the URL has valid Vimeo format, but also allow non-Modules to have empty URL
      const VIMEO_URL_REGEX = /(?:vimeo\.com\/(?:.*?\/)?(?:video\/)?)(\d+)/;
      const isValidVimeoUrl = videoUrl ? VIMEO_URL_REGEX.test(videoUrl) : true;

      // Check all code files required by selected language have been provided (compulsory only for modules)
      // Only require checking code name since zod schema does the rest, and necessary to save button to enable for unchanged code
      // NOTE: Initial code is now OPTIONAL for modules (UAT request). Final code remains compulsory.
      // --- REVERT INITIAL CODE COMPULSORY (BEGIN): restore the commented-out logic below to make initial code required again ---
      // const hasAllInitialCode = Boolean(initialCodeName);
      const hasAllFinalCode = Boolean(finalCodeName);

      let hasSuppliedAllCode = false;
      if (["Minecraft", "Micro:bit"].includes(codingLanguage)) {
        // Minecraft and Micro:bit only need final code
        hasSuppliedAllCode = hasAllFinalCode;
      } else if (["Python", "JavaScript", "Scratch"].includes(codingLanguage)) {
        // Python, JavaScript, and Scratch need final code; initial code is optional
        // hasSuppliedAllCode = hasAllInitialCode && hasAllFinalCode;
        hasSuppliedAllCode = hasAllFinalCode;
      } else {
        // For other languages, no code is required
        hasSuppliedAllCode = true;
      }
      // --- REVERT INITIAL CODE COMPULSORY (END) ---

      // Define the condition for two or three steps logic
      const toSave =
        selectedLesson === "modules"
          ? !!(
              name &&
              level &&
              description &&
              videoUrl &&
              isValidVimeoUrl &&
              codingLanguage &&
              hasSuppliedAllCode
            )
          : ["courses", "projects"].includes(selectedLesson)
            ? !!(name && level && description && isValidVimeoUrl)
            : !!(name && description);

      // Set show save button state
      setActivateSaveButton(toSave);

      // Set if to save or not
      toSave
        ? headerRef.current
            .options()
            .setSaveButton({ show: true, disabled: false })
        : headerRef.current
            .options()
            .setSaveButton({ show: true, disabled: true });
    }, [
      name,
      level,
      description,
      videoUrl,
      codingLanguage,
      headerRef,
      selectedLesson,
      initialCode,
      initialCodeName,
      initialCodeSize,
      finalCode,
      finalCodeName,
      finalCodeSize,
    ]);

    /**
     * Function to handle save pressing
     */
    const handleSave = async () => {
      // Check if it's currently saving
      if (isSaving) return;

      // Lock the saving function
      setIsSaving(true);

      // Everytime this function is called, update the version
      setVersion((currentVersion: number) => currentVersion + 1);

      // Get the data
      const data = provideData();

      // Define the current lesson to be checked
      // (This is because the lessonListToShow indicates the list of the "content")
      // tab. So the "current" one should be the parent of this. We are using this
      // instead of "activeLesson" because this is the general situation when we are
      // clicking edit consecutively in the cards.
      // The border case is the module one, when it's not showing nothing.
      const lessonToAdd = lessonListToShow
        ? lessonInformation(lessonListToShow).parent
        : "modules";

      // If there is a selectedData, then we are editing
      let response: {
        successful: boolean;
        message: string;
        _id?: string;
        headerImageUrl?: PresignedPost;
        badgeImageUrl?: PresignedPost;
        planDocumentUrl?: PresignedPost;
        initialCodeUrl?: PresignedPost;
        finalCodeUrl?: PresignedPost;
        instructionUrls?: { [key: string]: PresignedPost };
      } = { successful: false, message: "" };
      if (selectedData) {
        const { organisations, ...dataToUpdate } = data; // remove the organisations field when updating
        response = await updateRecordRequest(
          { _id: selectedData._id, toUpdate: dataToUpdate },
          `/api/${lessonToAdd}`,
        );
      } else {
        // Send the request to be created
        response = await createRecordRequest(
          { linkedLesson, linkedId, ...data },
          `/api/${lessonToAdd}`,
        );
      }

      // If the response is not successful, then show the pop up message
      if (!(response.successful && response._id)) {
        alert(response.message);
        // Release this function
        setIsSaving(false);
        return;
      }

      // First of all, set the id state
      setId(response._id);

      // If everything goes well, let's try to upload the media files
      await Promise.all([
        sendFilesToS3ByPresignedPost(
          headerImage,
          `${platform}/${lessonToAdd}/${response._id}/headerImage`,
          response.headerImageUrl,
        ),
        sendFilesToS3ByPresignedPost(
          badgeImage,
          `${platform}/${lessonToAdd}/${response._id}/badgeImage`,
          response.badgeImageUrl,
        ),
        sendFilesToS3ByPresignedPost(
          planDocument,
          `${platform}/${lessonToAdd}/${response._id}/planDocument`,
          response.planDocumentUrl,
        ),
        sendFilesToS3ByPresignedPost(
          initialCode,
          `${platform}/${lessonToAdd}/${response._id}/initialCode`,
          response.initialCodeUrl,
        ),
        sendFilesToS3ByPresignedPost(
          finalCode,
          `${platform}/${lessonToAdd}/${response._id}/finalCode`,
          response.finalCodeUrl,
        ),
      ]);

      // Upload the instruction images (if defined)
      await sendInstructionImagesToS3({
        platform,
        stepByStepInstructions,
        instructionUrls: response.instructionUrls,
        lessonToAdd,
        lessonId: response._id,
      });

      // In the case of the course create/edit, it is necessary to create
      // the new quizzes as well
      if (lessonToAdd === "courses") {
        await createUpdateQuizzesInCourses(
          response._id as string,
          quizzes,
          baseSelectedQuizzes,
          platform,
        );
      }

      // Release this function
      setIsSaving(false);

      // And proceed to check content for profanity
      if (!!selectedData) checkContentForProfanity(selectedData._id);
      else checkContentForProfanity(response._id);
    };

    /**
     * Function used to check the profanity of the content for
     * this specific lesson
     * @param _id
     * @returns
     */
    const checkContentForProfanity = async (_id: string) => {
      // Define the current lesson to be checked
      // (This is because the lessonListToShow indicates the list of the "content")
      // tab. So the "current" one should be the parent of this. We are using this
      // instead of "activeLesson" because this is the general situation when we are
      // clicking edit consecutively in the cards.
      // The border case is the module one, when it's not showing nothing.
      const lessonToAdd = lessonListToShow
        ? lessonInformation(lessonListToShow).parent
        : "modules";

      // First the content to be checked
      const contentResponse = await getCollectionRequest(
        `/api/lessons/content-to-check?lessonType=${lessonToAdd}&_id=${_id}`,
      );

      // Check the query went right
      if (!(contentResponse && contentResponse.successful)) return;

      // Get the list of content to be checked
      const contentToBeChecked = contentResponse.content;

      // Check if it's empty
      if (contentToBeChecked.length === 0) {
        // Just come back
        return afterCreateLessons(lessonToAdd);
      }

      // Transform it into the format
      const contentFormatted = contentToBeChecked.reduce(
        (output: { [content: string]: { status: string } }, item: string) => {
          output[item] = { status: "loading" };
          return output;
        },
        {},
      );

      // Set the content to be checked based on the content
      setContentToBeChecked(contentFormatted);

      // Check if it's currently validating
      if (isValidating) return;

      // Show the check content modal
      setIsValidating(true);

      // Get the data
      const response = await createRecordRequest(
        {
          _id,
          lessonType: lessonToAdd,
          action: !!selectedData ? "update" : "create",
        },
        "/api/lessons/check-content-profanity",
      );

      // If the response is not successful, then show the pop up message
      if (!response.successful) {
        alert(response.message);
        // Release this function
        setIsValidating(false);
        return;
      }

      // Get the reports
      const reports = response.content || {};

      // Transform the reports into a plain array of contents
      const contentReports = Object.keys(reports);

      // Validate the status for each element
      setContentToBeChecked((currentObj) => {
        const updatedObj = { ...currentObj };

        Object.keys(currentObj).forEach((content: string) => {
          // Get the object of the report
          const reportItem = reports[content];

          if (contentReports.includes(content)) {
            updatedObj[content] = {
              status: reportItem.status,
              report: reportItem.content,
            };
          } else {
            updatedObj[content] = { status: "successful" };
          }
        });

        return updatedObj;
      });

      // If it's a void array
      if (contentReports.length === 0) {
        // Create a timer to close this modal after this time
        const timer = setTimeout(() => {
          // Show the check content modal
          setIsValidating(false);

          // Execute the defined actions after the creation
          afterCreateLessons(lessonToAdd);
        }, 3000);

        // Clear the timer
        return () => clearTimeout(timer);
      }

      // Otherwise, just apply the after create function without going back
      afterCreateLessons(lessonToAdd, false);
    };

    const provideData = () => {
      // Return all the gathered data depending on the active lesson to be created
      let data: LessonListType = {
        title: "",
        headerImage: "",
        badgeImage: "",
        description: "",
      };

      if (selectedLesson === "programs") {
        data = {
          title: name,
          headerImage,
          badgeImage,
          description,
          organisations,
        };
      } else if (selectedLesson === "courses") {
        data = {
          title: name,
          headerImage,
          badgeImage,
          level,
          description,
          organisations,
        };
      } else if (selectedLesson === "projects") {
        data = {
          title: name,
          headerImage,
          badgeImage,
          level,
          experiencePoints: expPoints,
          description,
          planDocument,
          planName,
          videoUrl,
          language: codingLanguage,
          initialCode,
          finalCode,
          initialCodeName,
          finalCodeName,
          organisations,
          stepByStepInstructions: formatStepByStepInstructions(
            stepByStepInstructions,
          ),
        };
      } else if (selectedLesson === "modules") {
        data = {
          title: name,
          badgeImage,
          level,
          experiencePoints: expPoints,
          description,
          planDocument,
          planName,
          videoUrl,
          language: codingLanguage,
          initialCode,
          finalCode,
          initialCodeName,
          finalCodeName,
          organisations,
          stepByStepInstructions: formatStepByStepInstructions(
            stepByStepInstructions,
          ),
        };
      }

      // Formatting the data to be sent as a request
      const formattedData = formatUpdateLesson(selectedData, data);

      return formattedData;
    };

    /**
     * Function used to control the logic of closing the validation content
     * pop up based on the type of flow (create or edit)
     */
    const handleCloseValidationPopUp = () => {
      // Close the pop up
      setIsValidating(false);

      // And if there's no selected data (when creating a content),
      // make sure that now it's selected (to keep editing)
      if (!selectedData) {
        setSelectedData({
          _id: id,
          title: name,
          level,
          description,
          experiencePoints: expPoints,
          videoUrl,
          language: codingLanguage,
          headerImage: headerImage || "",
          badgeImage: badgeImage || "",
          planDocument: planDocument || "",
          planName,
          initialCodeName,
          finalCodeName,
        } as any);
      }
    };

    return (
      <CreateEditLessonContext.Provider
        value={{
          lessonId: id,
          lessonType: selectedLesson,
          planDocument,
          setPlanDocument,
          setPlanName,
          setPlanSize,
        }}
      >
        <div className="w-full h-full flex justify-center overflow-x-auto custom-scroll">
          <div className="flex justify-between items-start w-full max-w-lg">
            <div className="flex flex-col items-start">
              {stage === 1 ? (
                <BasicDetails
                  selectedLesson={selectedLesson}
                  name={name}
                  level={level}
                  expPoints={expPoints}
                  headerImage={headerImage}
                  selectedData={selectedData}
                  setName={setName}
                  setLevel={setLevel}
                  setExpPoints={setExpPoints}
                  setStage={setStage}
                  setHeaderImage={setHeaderImage}
                />
              ) : stage === 2 ? (
                <AdditionalDetails
                  selectedLesson={selectedLesson}
                  description={description}
                  additionalAttachs={additionalAttachs}
                  quizzes={quizzes}
                  badgeImage={badgeImage}
                  selectedData={selectedData}
                  setDescription={setDescription}
                  setQuizzes={setQuizzes}
                  setBadgeImage={setBadgeImage}
                  setStage={setStage}
                  onSave={handleSave}
                />
              ) : (
                <AdditionalAttachments
                  selectedLesson={selectedLesson}
                  selectedData={selectedData}
                  activateSaveButton={activateSaveButton}
                  videoUrl={videoUrl}
                  codingLanguage={codingLanguage}
                  planDocument={planDocument}
                  planName={planName}
                  planSize={planSize}
                  setVideoUrl={setVideoUrl}
                  setCodingLanguage={setCodingLanguage}
                  setPlanDocument={setPlanDocument}
                  setPlanName={setPlanName}
                  setPlanSize={setPlanSize}
                  setInitialCodeName={setInitialCodeName}
                  setInitialCodeSize={setInitialCodeSize}
                  setFinalCodeName={setFinalCodeName}
                  setFinalCodeSize={setFinalCodeSize}
                  initialCodeName={initialCodeName}
                  initialCodeSize={initialCodeSize}
                  finalCodeName={finalCodeName}
                  finalCodeSize={finalCodeSize}
                  setStage={setStage}
                  setInitialCode={setInitialCode}
                  setFinalCode={setFinalCode}
                  onSave={handleSave}
                  stepByStepInstructions={stepByStepInstructions}
                  setStepByStepInstructions={setStepByStepInstructions}
                />
              )}
            </div>
            {/* Progress map definition */}
            <ProgressMap
              stage={stage}
              steps={[
                "Basic details",
                "Additional details",
                ...(additionalAttachs ? ["Additional attachments"] : []),
              ]}
            />
            {/* Screen to wait until the lesson is created */}
            <AnimatePresence>
              {isSaving && (
                <SavingPopUp
                  key="saving-pop-up"
                  onBgClick={() => setIsSaving(false)}
                />
              )}
              {isValidating && (
                <ValidatingLessonContents
                  key="validating-pop-up"
                  lessonType={selectedLesson}
                  contentToBeChecked={contentToBeChecked}
                  onBgClick={() => {
                    /*setIsValidating(false)*/
                  }}
                  onCloseClick={handleCloseValidationPopUp}
                />
              )}
            </AnimatePresence>
          </div>
        </div>
      </CreateEditLessonContext.Provider>
    );
  },
);

export default Settings;
